import { getWallPath } from "building-generation/elements/walls/wall";
import type {
  ControllerPlacementCandidateTable,
  CornerControllerPlacementCandidate,
} from "building-types/electrical/candidate";
import { cornerCandidateNew } from "building-types/electrical/candidate";
import { InsertionWindingDirection } from "building-types/electrical/candidateTypesEnum";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallStrip } from "building-types/walls/wallStrip";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import type { RoomIdToRoomSetIndexLookup, RoomSetWithLevelId } from "doc/rooms/roomSetTypes";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { intervalsOverlap } from "geom/aabb2";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import {
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Length,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist, vec2Dot } from "geom/vec2";
import { cutGapsIntoIntervalStartingAtZero } from "../../../utils/intervals";
import { addCandidateToTable } from "./addCandidateToTable";

const PLATE_CLEARANCE_WIDTH = worldInchesNew(8);
const PLATE_CENTER_HEIGHT = worldInchesNew(48);
const PLATE_HEIGHT_DIM = worldInchesNew(4.5);
const HEIGHT_BUFFER = worldInchesNew(2);

export function candidatesCornerNew(
  levels: Table<Level>,
  wallSystems: Table<WallSystem>,
  roomsTable: Table<Room>,
  roomsByLevel: Table<string[]>,
  roomSets: RoomSetWithLevelId[],
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
): ControllerPlacementCandidateTable {
  const candidateTable: ControllerPlacementCandidateTable = {};

  const plateTop = worldInchesNew(PLATE_CENTER_HEIGHT + PLATE_HEIGHT_DIM / 2 + HEIGHT_BUFFER);
  const plateBottom = worldInchesNew(PLATE_CENTER_HEIGHT - PLATE_HEIGHT_DIM / 2 - HEIGHT_BUFFER);

  for (const levelId in levels) {
    const wallSystem = wallSystems[levelId];
    const levelRoomSets = roomSets.filter((x) => x.levelId === levelId);
    const rooms = roomsOnLevel(levelId, roomsTable, roomsByLevel);
    const wallIndex = wallSystem.wallIndex;

    for (const { roomSet, id } of levelRoomSets) {
      const roomSetStrips = wallIndex.solidWallStripsInRoomSet(roomSet, roomsTable, rooms, true);

      for (const stripIndexString of Object.keys(roomSetStrips)) {
        const stripIndex = parseInt(stripIndexString);

        candidatesConcaveCornerNew({
          plateTop,
          plateBottom,
          plateClearanceWidth: PLATE_CLEARANCE_WIDTH,
          roomIdToRoomSetIndexLookup,
          standardCornerOffset: worldInchesNew(0),
          roomSetStrips,
          stripIndex,
          candidateTable,
          setIndex: id,
          levelId,
        });
      }
    }
  }

  return candidateTable;
}

interface CandidatesConcaveCornerNewInfo {
  stripIndex: number;
  roomSetStrips: WallStrip[];
  plateTop: WorldInches;
  plateBottom: WorldInches;
  plateClearanceWidth: WorldInches;
  standardCornerOffset: WorldInches;
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup;
  candidateTable: ControllerPlacementCandidateTable;
  setIndex: number;
  levelId: string;
}

export function candidatesConcaveCornerNew({
  roomSetStrips,
  plateTop,
  plateBottom,
  plateClearanceWidth,
  standardCornerOffset,
  stripIndex,
  roomIdToRoomSetIndexLookup,
  candidateTable,
  setIndex,
  levelId,
}: CandidatesConcaveCornerNewInfo) {
  const setLen = roomSetStrips.length;

  const currentStrip = roomSetStrips[stripIndex];
  const nextStrip = roomSetStrips[(stripIndex + 1) % setLen];

  const currentEdge = currentStrip.baseline(true);
  const nextEdge = nextStrip.baseline(true);

  // if strips not continuous (e.g. porches), don't check last to first index corner
  if (stripIndex === setLen - 1) {
    const current = currentStrip.centerLine(true);
    const next = nextStrip.centerLine(true);
    const [x1, y1] = polyLine2End(current);
    const [x2, y2] = polyLine2Start(next);
    if (vec2Dist(x1, y1, x2, y2) > DIST_EPSILON) {
      return;
    }
  }

  const [edgeNormalX, edgeNormalY] = polyLine2EdgeNormal(currentEdge, 0);
  const [nextEdgeDirX, nextEdgeDirY] = polyLine2EdgeTangent(nextEdge, 0);
  const endDot = vec2Dot(edgeNormalX, edgeNormalY, nextEdgeDirX, nextEdgeDirY);

  // convex corner text
  if (endDot <= -DIST_EPSILON) {
    return;
  } else if (Math.abs(endDot) < DIST_EPSILON) {
    const lastWallOfCurrent = currentStrip.lastWall();
    const firstWallOfNext = nextStrip.firstWall();
    if (!lastWallOfCurrent || !firstWallOfNext || lastWallOfCurrent.id !== firstWallOfNext.id) {
      // wall does not return back on itself
      return;
    }
  }

  const bkwdCandidate = backwardFromConvexCorner(
    currentStrip,
    plateTop,
    plateBottom,
    currentEdge,
    plateClearanceWidth,
    standardCornerOffset,
    roomIdToRoomSetIndexLookup,
    levelId,
    setIndex,
  );
  if (bkwdCandidate) {
    addCandidateToTable(bkwdCandidate, candidateTable, setIndex);
  }

  const fwdCandidate = forwardFromConvexCorner(
    nextStrip,
    plateTop,
    plateBottom,
    nextEdge,
    plateClearanceWidth,
    standardCornerOffset,
    roomIdToRoomSetIndexLookup,
    levelId,
    setIndex,
  );
  if (fwdCandidate) {
    addCandidateToTable(fwdCandidate, candidateTable, setIndex);
  }
}

function forwardFromConvexCorner(
  nextStrip: WallStrip,
  plateTop: number,
  plateBottom: number,
  nextEdge: PolyLine2,
  plateClearanceWidth: number,
  standardCornerOffset: number,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  levelId: string,
  setIndex: number,
): CornerControllerPlacementCandidate | undefined {
  const doorGaps = nextStrip
    .doorGaps(false)
    .filter((x) => intervalsOverlap(plateTop, plateBottom, x.bottom, x.bottom + x.height))
    .sort((a, b) => a.start - b.start);
  const windowGaps = nextStrip
    .windowGaps(false)
    .filter((x) => intervalsOverlap(plateTop, plateBottom, x.bottom, x.bottom + x.height))
    .sort((a, b) => a.start - b.start);
  const gapIntervals: number[] = [];
  for (const { start, end } of doorGaps) {
    gapIntervals.push(start);
    gapIntervals.push(end);
  }
  for (const { start, end } of windowGaps) {
    gapIntervals.push(start);
    gapIntervals.push(end);
  }
  gapIntervals.sort((a, b) => a - b);
  const nextLengthDim = polyLine2Length(nextEdge);
  const nextGaps = cutGapsIntoIntervalStartingAtZero(nextLengthDim, gapIntervals);
  const startT = nextGaps[0];
  const endT = nextGaps[1];
  if (endT - startT >= plateClearanceWidth + standardCornerOffset) {
    const placementTOnStrip = startT + (plateClearanceWidth / 2 + standardCornerOffset);
    const stripWallsBaseline = nextStrip.baseline(false);
    const stripJoinedBaseline = nextEdge;
    const [nX, nY] = polyLine2EdgeNormal(stripJoinedBaseline, 0, true);
    const walls = nextStrip.toWallArray();
    const [joinedX, joinedY] = polyLine2EdgeEval(stripJoinedBaseline, 0, placementTOnStrip, false);
    const { edgeIndex } = closestPointPolyLine2(stripWallsBaseline, joinedX, joinedY);
    if (!walls[edgeIndex]) {
      return undefined;
    }
    const { leftRoom, rightRoom } = getWallPath(walls[edgeIndex]);
    const room = leftRoom && roomIdToRoomSetIndexLookup[leftRoom.id] === setIndex ? leftRoom : rightRoom;
    const roomId = room ? room.id : undefined;

    return cornerCandidateNew({
      levelId,
      roomId,
      insertionDirection: InsertionWindingDirection.FORWARD,
      x: joinedX,
      y: joinedY,
      nX,
      nY,
    });
  }
  return undefined;
}

function backwardFromConvexCorner(
  currentStrip: WallStrip,
  plateTop: number,
  plateBottom: number,
  currentEdge: PolyLine2,
  plateClearanceWidth: number,
  standardCornerOffset: number,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  levelId: string,
  setIndex: number,
): CornerControllerPlacementCandidate | undefined {
  const doorGaps = currentStrip
    .doorGaps(false)
    .filter((x) => intervalsOverlap(plateTop, plateBottom, x.bottom, x.bottom + x.height))
    .sort((a, b) => a.start - b.start);

  const windowGaps = currentStrip
    .windowGaps(false)
    .filter((x) => intervalsOverlap(plateTop, plateBottom, x.bottom, x.bottom + x.height))
    .sort((a, b) => a.start - b.start);

  const gapIntervals: number[] = [];
  for (const { start, end } of doorGaps) {
    gapIntervals.push(start);
    gapIntervals.push(end);
  }
  for (const { start, end } of windowGaps) {
    gapIntervals.push(start);
    gapIntervals.push(end);
  }

  gapIntervals.sort((a, b) => a - b);
  const currentLengthDim = polyLine2Length(currentEdge);
  const currentGaps = cutGapsIntoIntervalStartingAtZero(currentLengthDim, gapIntervals);
  const len = currentGaps.length;
  const startT = currentGaps[len - 2];
  const endT = currentGaps[len - 1];

  if (endT - startT >= plateClearanceWidth + standardCornerOffset) {
    const placementTOnStrip = endT - (plateClearanceWidth / 2 + standardCornerOffset);
    const stripWallsBaseline = currentStrip.baseline(false);
    const stripJoinedBaseline = currentEdge;
    const [nX, nY] = polyLine2EdgeNormal(stripJoinedBaseline, 0, true);
    const walls = currentStrip.toWallArray();
    const [joinedX, joinedY] = polyLine2EdgeEval(stripJoinedBaseline, 0, placementTOnStrip, false);
    const { edgeIndex } = closestPointPolyLine2(stripWallsBaseline, joinedX, joinedY);
    if (!walls[edgeIndex]) {
      return undefined;
    }
    const { leftRoom, rightRoom } = getWallPath(walls[edgeIndex]);
    const room = leftRoom && roomIdToRoomSetIndexLookup[leftRoom.id] === setIndex ? leftRoom : rightRoom;
    const roomId = room ? room.id : undefined;

    return cornerCandidateNew({
      levelId,
      roomId,
      insertionDirection: InsertionWindingDirection.BACKWARD,
      x: joinedX,
      y: joinedY,
      nX,
      nY,
    });
  }
  return undefined;
}
